import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../providers/AuthProvider';
import TalentPortal from '../../../talent-portal/TalentPortal';
import DashboardCard, { LastUpdatedPill } from './DashboardCard';

/**
 * Preferences card for the new talent dashboard.
 * Functional / Tailwind port of PreferencesPanel, plus a read-only summary of the
 * talent's saved work preferences (same fields the /talent/work-preferences page edits).
 */

// Grouped chip labels for the talent_work_preferences rating keys (see User.GroupedTalentWorkPreferences)
const RATING_GROUPS = [
    {
        label: 'Length',
        options: {
            project_short_length: '< 1 month',
            project_long_length: '1-3 months',
            project_full_time: '3+ months',
        },
    },
    {
        label: 'Pricing',
        options: {
            contract_fixed_fee: 'Fixed price',
            contract_time_materials: 'Time & materials',
        },
    },
];

// Ratings are 1–5 and default to 1 when unset, so only ratings above 1 count as a preference
const topRatedLabels = (ratings = {}, options) => {
    const scores = TB.mapValues(options, (label, key) => parseInt(ratings?.[key]) || 1);
    const best = TB.max(TB.values(scores));
    if (!best || best <= 1) return [];

    return TB.keys(options).filter(key => scores[key] === best).map(key => options[key]);
};

const timeZoneValues = (auth) => {
    if (auth.prefers_any_timezone) return ['Any'];

    const values = TB.filter(TB.TimeZonesInUS(), zone => (auth.time_zones || []).includes(zone.id))
        .map(zone => zone.name);

    if (auth.prefers_any_non_us_timezone) values.push('Global (non-U.S.)');

    return values;
};

/**
 * Returns the talent's saved work preferences as [{ label, values }] groups,
 * skipping any group with nothing set.
 */
const getPreferenceGroups = (auth) => {
    const groups = RATING_GROUPS.map(({ label, options }) => ({
        label,
        values: topRatedLabels(auth.talent_work_preferences, options),
    }));

    groups.push({
        label: 'Location',
        values: TB.isDefined(auth.can_travel_for_work) && auth.can_travel_for_work !== null
            ? [auth.can_travel_for_work ? 'Open to on-site' : 'Remote']
            : [],
    });

    groups.push({ label: 'Time zone', values: timeZoneValues(auth) });
    // groups.push({ label: 'Open to permanent', values: [auth.has_full_time_employment_interest ? 'Yes' : 'No'] });
    // groups.push({ label: 'Typical hrs/wk', values: [`${auth.available_hours_per_week} hours`] });

    return groups.filter(group => group.values.length > 0);
};

const PreferencesCard = ({ className, title = 'Preferences' }) => {
    const { auth } = useAuth();

    const preferenceGroups = useMemo(() => (auth ? getPreferenceGroups(auth) : []), [
        auth,
        auth?.talent_work_preferences,
        auth?.time_zones,
        auth?.can_travel_for_work,
        auth?.prefers_any_timezone,
        auth?.prefers_any_non_us_timezone,
    ]);

    if (!auth) return null;

    return (
        <DashboardCard
            title={title}
            className={className}
            headerRight={<LastUpdatedPill days={auth.lastUpdatedWorkPreferences?.()} />}
            footer={(
                <Link
                    to={TalentPortal.preferencesPath()}
                    className="ml-auto inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-white hover:bg-primary-dark hover:text-white hover:no-underline"
                >
                    Edit preferences
                </Link>
            )}
        >
            <p className="m-0 text-sm leading-relaxed text-gray-500">
                Tell us the work you want — assignment types, length and time zones — so matches fit.
            </p>

            {preferenceGroups.length > 0 && (
                <dl className="m-0 mt-5 flex flex-col gap-3" aria-label="Your work preferences">
                    {preferenceGroups.map(group => (
                        <div key={group.label} className="grid grid-cols-[80px_minmax(0,1fr)] items-start gap-3">
                            <dt className="pt-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                                {group.label}
                            </dt>
                            <dd className="m-0 flex flex-wrap gap-1.5">
                                {group.values.map(value => (
                                    <span key={value} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-800">
                                        {value}
                                    </span>
                                ))}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </DashboardCard>
    );
};

PreferencesCard.propTypes = {
    className: PropTypes.string,
    title: PropTypes.node,
};

export default PreferencesCard;
